export type HeadingNumbering = {
  paragraphId: string
  /** Auto-generated section number from Word list formatting (e.g. "2.1" or "2.1."). */
  sectionNumber: string
  text: string
}

export type PlainTextParagraph = {
  text: string
  sectionNumber?: string
}

/**
 * Word does not document a fixed `getHtml()` shape for headings (output varies by platform;
 * MS recommends OOXML for fidelity). In CSR documents, numbered headings typically look like:
 *
 *   <h3 id="{uniqueLocalId}">
 *     <a name="_Toc…">                           ← optional TOC bookmark
 *       <span lang="EN-US">{listString}          ← first span: list number
 *         <span style="font:7.0pt …">…</span>    ← tab spacing
 *       </span>
 *       <span lang="EN-US">{title}</span>        ← second span: title
 *     </a>
 *   </h3>
 *
 * Without a TOC anchor the same two-span pattern sits directly on the heading.
 * `listItem.listString` from the Word API is authoritative.
 */
function findListNumberSpan(heading: Element, title: string): HTMLSpanElement | null {
  const ignoreSpan = heading.querySelector(
    'span[style*="mso-list:Ignore"], span[style*="mso-list:ignore"]'
  )
  if (ignoreSpan instanceof HTMLSpanElement) return ignoreSpan

  const firstSpan = heading.querySelector('span')
  if (!(firstSpan instanceof HTMLSpanElement)) return null

  const isLoneTitleSpan =
    heading.querySelectorAll('span').length === 1 && firstSpan.textContent?.trim() === title.trim()
  return isLoneTitleSpan ? null : firstSpan
}

/** Replaces the list-number text inside a span, keeping tab-filler / spacing child spans. */
function setListNumberSpanText(span: HTMLSpanElement, sectionNumber: string): void {
  const spacingChild = span.querySelector('span[style*="mso-tab-count"], span[style*="font:7"]')
  const numberText = [...span.childNodes].find((node) => node.nodeType === Node.TEXT_NODE) as
    Text | undefined

  if (numberText) {
    numberText.textContent = sectionNumber
  } else {
    span.insertBefore(span.ownerDocument.createTextNode(sectionNumber), spacingChild)
  }
}

function fixHeadingElement(heading: Element, sectionNumber: string, title: string): void {
  const listNumberSpan = findListNumberSpan(heading, title)

  if (listNumberSpan) {
    while (heading.firstChild?.nodeType === Node.TEXT_NODE) {
      heading.removeChild(heading.firstChild)
    }
    setListNumberSpanText(listNumberSpan, sectionNumber)
    return
  }

  const child = heading.firstChild
  const isLoneTitleTextNode =
    heading.childNodes.length === 1 &&
    child?.nodeType === Node.TEXT_NODE &&
    (child as Text).textContent?.trim() === title.trim()

  if (!isLoneTitleTextNode) {
    // Word's getHtml() shape for this heading didn't match either known pattern
    return
  }

  ;(child as Text).textContent = `${sectionNumber} ${title.trim()}`
}

/** Prepends a section number to one plain-text paragraph, preserving trailing paragraph marks. */
export function formatPlainTextParagraph({ text, sectionNumber }: PlainTextParagraph): string {
  const trimmedSectionNumber = sectionNumber?.trim()
  if (!trimmedSectionNumber) return text

  // Word's paragraph.text includes the trailing \r paragraph mark, and paragraphs are rejoined
  // with '' — losing it here would merge this line into the next one.
  const paragraphMarkMatch = text.match(/[\r\n]+$/)
  const paragraphMark = paragraphMarkMatch?.[0] ?? ''
  const content = paragraphMark ? text.slice(0, -paragraphMark.length) : text

  return `${trimmedSectionNumber} ${content.trim()}${paragraphMark}`
}

/** Rebuilds plain text from paragraphs, prepending section numbers on built-in heading lines. */
export function buildPlainTextWithHeadingNumbers(paragraphs: PlainTextParagraph[]): string {
  return paragraphs.map(formatPlainTextParagraph).join('')
}

/**
 * Fixes Word auto-generated section numbers in heading HTML matched by paragraph id.
 * Rewrites the leading list-number span with listItem.listString, preserving title markup.
 */
export function injectHeadingNumbers(html: string, headings: HeadingNumbering[]): string {
  if (headings.length === 0) return html

  // getHtml() often returns a full document (<html>/<head>/<body>); parsing it wrapped in a div
  // lets the browser reparent <head> content (e.g. Word's <style> block) into the wrapper, which
  // then leaks into the output undetected downstream. Parse directly and use doc.body instead,
  // matching normalizeToCanonical's approach.
  const doc = new DOMParser().parseFromString(html, 'text/html')
  if (!doc.body) return html

  for (const { paragraphId, sectionNumber, text } of headings) {
    const trimmedNumber = sectionNumber.trim()
    if (!trimmedNumber) continue

    const heading = doc.getElementById(paragraphId)
    if (!heading) continue

    fixHeadingElement(heading, trimmedNumber, text)
  }

  return doc.body.innerHTML
}
